<script setup lang="ts">
  import { computed, watch } from 'vue';
  import { Clipboard, Copy, Pencil, Trash2 } from 'lucide-vue-next';
  import CheckMarkIcon from '@/shared/assets/icons/CheckMarkIcon.svg';
  import CheckboxCheckIcon from '@/shared/assets/icons/CheckboxCheckIcon.svg';
  import { ColoredButton, IconImage, SimpleLoading, TabsMenu } from '@/shared/ui';
  import DirectoriesActionsPlaceholder from './DirectoriesActionsPlaceholder.vue';
  import DirectoriesAddMenu from './DirectoriesAddMenu.vue';
  import DirectoriesConfigureTab from './DirectoriesConfigureTab.vue';
  import DirectoriesDeleteButton from './DirectoriesDeleteButton.vue';
  import DirectoriesPermissionFooterButton from './DirectoriesPermissionFooterButton.vue';
  import DirectoriesPermissionsTab from './DirectoriesPermissionsTab.vue';
  import { DIRECTORY_NODE_COPY } from '../config/directoryNodeCopy';
  import { PERMISSIONS_LOADING_MESSAGE, PERMISSIONS_UNAVAILABLE_MESSAGE } from '../config/messages';
  import { formatSelectionCount } from '../model/selectionCount';
  import type {
    DirectoryActionsPanelTab,
    DirectoryNodeDetails,
    DirectoryNodePermissions,
    DirectoryTreeNode,
  } from '../model/types';

  const props = withDefaults(
    defineProps<{
      hasSelection: boolean;
      activeTab: DirectoryActionsPanelTab;
      canViewPermissionsTab: boolean;
      canToggleSuperadmin?: boolean;
      iconModelValue: string;
      isSaveDisabled?: boolean;
      titleError?: string;
      node?: DirectoryTreeNode;
      details?: DirectoryNodeDetails;
      permissions?: DirectoryNodePermissions | null;
      isLoadingPermissions?: boolean;
      selectedCount?: number;
      isDeleteButtonVisible?: boolean;
      deleteLockMessage?: string | null;
      canApplyPositions?: boolean;
      canClearPositions?: boolean;
      canApplyFunctionalActivities?: boolean;
      canClearFunctionalActivities?: boolean;
      canPastePermissions?: boolean;
    }>(),
    {
      isLoadingPermissions: false,
      selectedCount: 1,
      isDeleteButtonVisible: true,
      deleteLockMessage: null,
      canApplyPositions: false,
      canClearPositions: false,
      canApplyFunctionalActivities: false,
      canClearFunctionalActivities: false,
      canPastePermissions: false,
    }
  );

  const isBulkSelection = computed(() => props.selectedCount > 1);

  const selectionCountLabel = computed(
    () => `${formatSelectionCount(props.selectedCount)} itens selecionados`
  );

  const nodeCopy = computed(() => (props.node ? DIRECTORY_NODE_COPY[props.node.kind] : undefined));

  const isTitleLocked = computed(() => props.node?.matrixPlacementLocked === true);

  const isSaveButtonVisible = computed(
    () => (nodeCopy.value?.showSaveButton ?? false) && !isTitleLocked.value
  );

  const isAddMenuVisible = computed(() => nodeCopy.value?.showAddMenu ?? false);

  const canAddDocument = computed(() => props.node?.folderContentType !== 'MATRIX');

  const isPermissionsFooterVisible = computed(
    () => props.activeTab === 'permissions' && props.canViewPermissionsTab && !!props.permissions
  );

  const emit = defineEmits<{
    (e: 'update:activeTab', tab: DirectoryActionsPanelTab): void;
    (e: 'update:title', value: string): void;
    (e: 'update:iconId', value: string): void;
    (e: 'update:archived', value: boolean): void;
    (e: 'toggle-superadmin'): void;
    (e: 'toggle-position', positionId: string): void;
    (e: 'toggle-functional-activity', activityId: string): void;
    (e: 'toggle-positions-select-all', positionIds: string[]): void;
    (e: 'toggle-functional-activities-select-all', activityIds: string[]): void;
    (e: 'apply-positions'): void;
    (e: 'apply-functional-activities'): void;
    (e: 'clear-positions'): void;
    (e: 'clear-functional-activities'): void;
    (e: 'copy-permissions'): void;
    (e: 'paste-permissions'): void;
    (e: 'save'): void;
    (e: 'add-child'): void;
    (e: 'add-record'): void;
    (e: 'add-document'): void;
    (e: 'delete'): void;
  }>();

  const tabOptions = computed<{ id: DirectoryActionsPanelTab; text: string }[]>(() => [
    { id: 'configure', text: 'Configurar' },
    ...(props.canViewPermissionsTab ? [{ id: 'permissions' as const, text: 'Permissões' }] : []),
  ]);

  watch(
    () => props.activeTab === 'permissions' && !props.canViewPermissionsTab,
    (hasLostAccess) => {
      if (hasLostAccess) {
        emit('update:activeTab', 'configure');
      }
    },
    { immediate: true }
  );

  const handleTabSelect = (optionId: string) => {
    if (optionId === 'configure' || optionId === 'permissions') {
      emit('update:activeTab', optionId);
    }
  };
</script>

<template>
  <aside
    data-testid="directories-actions-panel"
    class="flex h-full max-h-full min-h-0 w-[400px] shrink-0 flex-col border-l border-gray-02 bg-white-01"
  >
    <div data-testid="directories-actions-panel-header" class="shrink-0">
      <div class="border-b border-gray-02 p-4">
        <h4 class="text-sm font-semibold text-gray-07">Ações</h4>
      </div>
      <div class="border-b border-gray-02 px-4 pt-6 pb-4">
        <TabsMenu
          :options="tabOptions"
          :selected-option="activeTab"
          visual-variant="neutral"
          @option-selected="handleTabSelect"
        />
      </div>
    </div>

    <div
      v-if="isBulkSelection"
      data-testid="directories-selection-count"
      class="shrink-0 px-4 pt-4"
    >
      <div
        data-testid="directories-permissions-selected-count"
        class="flex w-full items-center gap-2 rounded-lg border border-gray-02 bg-gray-02/30 px-3 py-2 shadow-[0_1px_2px_rgba(16,24,40,0.05)]"
      >
        <IconImage :src="CheckboxCheckIcon" alt="" class="size-6 shrink-0 text-gray-03" />
        <span class="text-sm leading-5 text-gray-03">{{ selectionCountLabel }}</span>
      </div>
    </div>

    <div
      data-testid="directories-actions-panel-body"
      class="min-h-0 flex-1 overflow-y-auto px-4 py-4"
    >
      <DirectoriesActionsPlaceholder v-if="!hasSelection" />
      <template v-else>
        <DirectoriesConfigureTab
          v-if="activeTab === 'configure' && node && details"
          :node-id="node.id"
          :node-kind="node.kind"
          :details="details"
          :icon-model-value="iconModelValue"
          :title-error="titleError"
          :title-locked="isTitleLocked"
          @update:title="emit('update:title', $event)"
          @update:icon-id="emit('update:iconId', $event)"
          @update:archived="emit('update:archived', $event)"
        />
        <DirectoriesPermissionsTab
          v-else-if="activeTab === 'permissions' && canViewPermissionsTab && permissions"
          :permissions="permissions"
          :can-toggle-superadmin="canToggleSuperadmin ?? false"
          :can-apply-positions="canApplyPositions"
          :can-clear-positions="canClearPositions"
          :can-apply-functional-activities="canApplyFunctionalActivities"
          :can-clear-functional-activities="canClearFunctionalActivities"
          @toggle-superadmin="emit('toggle-superadmin')"
          @toggle-position="emit('toggle-position', $event)"
          @toggle-functional-activity="emit('toggle-functional-activity', $event)"
          @toggle-positions-select-all="emit('toggle-positions-select-all', $event)"
          @toggle-functional-activities-select-all="
            emit('toggle-functional-activities-select-all', $event)
          "
          @apply-positions="emit('apply-positions')"
          @apply-functional-activities="emit('apply-functional-activities')"
          @clear-positions="emit('clear-positions')"
          @clear-functional-activities="emit('clear-functional-activities')"
        />
        <div
          v-else-if="activeTab === 'permissions' && canViewPermissionsTab && isLoadingPermissions"
          data-testid="directories-permissions-loading"
          class="flex items-center justify-center gap-2 py-6 text-sm text-gray-05"
        >
          <SimpleLoading />
          {{ PERMISSIONS_LOADING_MESSAGE }}
        </div>
        <p
          v-else-if="activeTab === 'permissions' && canViewPermissionsTab"
          data-testid="directories-permissions-unavailable"
          class="py-6 text-center text-sm text-gray-05"
        >
          {{ PERMISSIONS_UNAVAILABLE_MESSAGE }}
        </p>
      </template>
    </div>

    <template v-if="hasSelection">
      <div
        v-if="isPermissionsFooterVisible"
        data-testid="directories-permissions-footer"
        class="flex shrink-0 gap-2 border-t border-gray-02 px-4 py-4"
      >
        <DirectoriesPermissionFooterButton
          text="Copiar permissões"
          :icon="Copy"
          @click="emit('copy-permissions')"
        />
        <DirectoriesPermissionFooterButton
          text="Colar permissões"
          :icon="Clipboard"
          :disabled="!canPastePermissions"
          @click="emit('paste-permissions')"
        />
      </div>

      <div
        v-else-if="isBulkSelection"
        data-testid="directories-actions-panel-bulk-footer"
        class="flex shrink-0 items-center justify-end gap-2 border-t border-gray-02 px-4 py-4"
      >
        <button
          type="button"
          disabled
          aria-label="Editar"
          class="flex size-10.5 shrink-0 cursor-not-allowed items-center justify-center rounded-lg border border-gray-16 text-gray-16"
        >
          <Pencil class="size-5" />
        </button>
        <DirectoriesDeleteButton
          v-if="isDeleteButtonVisible"
          :lock-message="deleteLockMessage"
          @delete="emit('delete')"
        />
        <button
          v-else
          type="button"
          disabled
          aria-label="Excluir"
          class="flex size-10.5 shrink-0 cursor-not-allowed items-center justify-center rounded-lg border border-gray-16 text-gray-16"
        >
          <Trash2 class="size-5" />
        </button>
      </div>

      <div
        v-else-if="activeTab === 'configure'"
        class="flex shrink-0 items-center justify-end gap-2 border-t border-gray-02 px-4 py-4"
      >
        <ColoredButton
          v-if="isSaveButtonVisible"
          class="flex-1 justify-center"
          data-testid="configure-save-button"
          text="Salvar"
          :icon="CheckMarkIcon"
          icon-alt="Salvar"
          :disabled="isSaveDisabled"
          @click="emit('save')"
        />
        <DirectoriesAddMenu
          v-if="isAddMenuVisible"
          :can-add-document="canAddDocument"
          @create-folder="emit('add-child')"
          @add-record="emit('add-record')"
          @add-document="emit('add-document')"
        />
        <DirectoriesDeleteButton
          v-if="isDeleteButtonVisible"
          :lock-message="deleteLockMessage"
          @delete="emit('delete')"
        />
      </div>
    </template>
  </aside>
</template>
